import type {
	EmbassyOfficeFields,
	EmbassyOfficeValues,
} from '@gov-nx/core/hooks';
import {
	useEmbassyOfficeFields,
	useEmbassyOfficeSelects,
} from '@gov-nx/core/hooks';
import type { FormDefinition2 } from '@gov-nx/core/service';
import { useFormBuilder } from '@gov-nx/core/service';
import { useKorespondencniVolbyLocale } from '@gov-nx/module/service';
import type { FormInputProps, FormRadioProps } from '@gov-nx/ui/types';
import { getRadioDefinition, useInputDefinition } from '@gov-nx/utils/common';

export interface KorespondencniHlasovaniDokumentyStep2 extends EmbassyOfficeValues {
	pickUpMethod?: 'POSTA' | 'OSOBNE';
	street: FormInputProps;
	city: FormInputProps;
	zip: FormInputProps;
	country: FormInputProps;
}

interface Fields extends EmbassyOfficeFields {
	pickUpMethod: FormRadioProps;
	street: FormInputProps;
	city: FormInputProps;
	zip: FormInputProps;
	country: FormInputProps;
}

export type KorespondencniHlasovaniDokumentyFormStep2 = FormDefinition2<
	KorespondencniHlasovaniDokumentyStep2,
	Fields
>;

export const useFormStep2 = ({ isDisabled }: { isDisabled: boolean }) => {
	const { t } = useKorespondencniVolbyLocale();

	const { isLoading, fields } = useEmbassyOfficeFields({
		isDisabled,
	});

	const pickUpMethod = getRadioDefinition({
		name: 'pickUpMethod',
		label: t('dokumenty.formular.zpusob-prevzeti.label'),
		isRequired: true,
		isRequiredMessage: t('dokumenty.formular.zpusob-prevzeti.validace.povinny'),
		options: [
			{ label: t('dokumenty.formular.zpusob-prevzeti.osobne'), name: 'OSOBNE' },
			{ label: t('dokumenty.formular.zpusob-prevzeti.posta'), name: 'POSTA' },
		],
		defaultValue: null,
	});

	const street = useInputDefinition({
		name: 'street',
		label: t('formular.ulice.label'),
		placeholder: t('formular.ulice.placeholder'),
		isDisabled,
		isRequiredWhen: {
			fieldName: 'pickUpMethod',
			is: 'POSTA',
			errorMessage: t('formular.ulice.validace.povinny'),
		},
	});
	const city = useInputDefinition({
		name: 'city',
		label: t('formular.mesto.label'),
		placeholder: t('formular.mesto.placeholder'),
		isDisabled,
		isRequiredWhen: {
			fieldName: 'pickUpMethod',
			is: 'POSTA',
			errorMessage: t('formular.mesto.validace.povinny'),
		},
	});
	const zip = useInputDefinition({
		name: 'zip',
		label: t('formular.psc.label'),
		placeholder: t('formular.psc.placeholder'),
		isDisabled,
		isRequiredWhen: {
			fieldName: 'pickUpMethod',
			is: 'POSTA',
			errorMessage: t('formular.psc.validace.povinny'),
		},
	});
	const country = useInputDefinition({
		name: 'country',
		label: t('formular.stat.label'),
		placeholder: t('formular.stat.placeholder'),
		isDisabled,
		isRequiredWhen: {
			fieldName: 'pickUpMethod',
			is: 'POSTA',
			errorMessage: t('formular.stat.validace.povinny'),
		},
	});

	const form = useFormBuilder<KorespondencniHlasovaniDokumentyStep2, Fields>([
		...fields,
		pickUpMethod,
		street,
		city,
		zip,
		country,
	]);

	const updatedForm = useEmbassyOfficeSelects(form, isDisabled);

	return {
		isLoading: isLoading,
		pickUpMethod: updatedForm.form.formMethods.watch('pickUpMethod'),
		...updatedForm,
	};
};
